import { Controller, useForm } from "react-hook-form";
import {
  alpha,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import { useContext, useEffect, useRef, useState } from "react";
import axios from "../../api/axios";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import logo from "../../utils/images/ridePlus.png";
import AuthContext from "../../context/AuthProvider";
import { navigatePostAuth } from "../../utils/postAuthNavigation";
import ProfilePicture from "../profile/profile-picture";
import TenantSelection from "../tenants/tenant-selection";
import CustomPinInput from "../../components/customPinInput";
import { phoneRegex } from "../../utils/validationPattern";
import useOtpResendCooldown from "../../hooks/useOtpResendCooldown";
import OtpResendButton from "../../components/otpResendButton";
import { queryClient } from "../../App";
import useTenantBranding from "../../hooks/useTenantBranding";

function Register({ consentId, headerBranding }) {
  // USESTATE & USEREF
  const [openDrawer, setOpenDrawer] = useState(false);
  const [phone, setPhone] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [inputType, setInputType] = useState("password");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmInputType, setConfirmInputType] = useState("password");
  const [file, setFile] = useState("");
  const [open, setOpen] = useState(false);
  const [requestOtp, setRequestOtp] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [showTenantSelection, setShowTenantSelection] = useState(false);
  const [userIdentifier, setUserIdentifier] = useState("");
  const [identifierType, setIdentifierType] = useState("phone"); // "phone" or "email"
  const [tosAndPPUrl, setTosAndPPUrl] = useState(null);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [pendingOtpAfterTenantSelect, setPendingOtpAfterTenantSelect] =
    useState(false);

  const password = useRef({});
  const userData = useRef({});

  // Cooldown hook for OTP resend functionality
  const cooldownHook = useOtpResendCooldown("register", phone);

  const {
    redirectUri,
    currentTenant,
    getTenantIdFromUrl,
    setAuth,
    setAuthWithTenant,
  } = useContext(AuthContext);
  const { tenantLogo, hideDefaultBranding } = useTenantBranding();

  const tenantId = currentTenant?.id || getTenantIdFromUrl();
  // NAVIGATE & SNACKBAR
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { enqueueSnackbar } = useSnackbar();
  const redirectSearchParams = new URLSearchParams(redirectUri?.search || "");
  const consentIdFromUrl =
    consentId || searchParams.get("consent_id") || searchParams.get("consentId");
  const clientIdFromUrl =
    searchParams.get("client_id") ||
    searchParams.get("clientId") ||
    redirectSearchParams.get("client_id") ||
    redirectSearchParams.get("clientId") ||
    location.state?.client_id ||
    location.state?.clientId;
  const tenantIdFromUrl =
    searchParams.get("tenant_id") ||
    searchParams.get("tenantId") ||
    redirectSearchParams.get("tenant_id") ||
    redirectSearchParams.get("tenantId") ||
    location.state?.tenant_id ||
    location.state?.tenantId;
  const effectiveTenantId = tenantId || tenantIdFromUrl;
  const hasClientAndTenant = !!clientIdFromUrl && !!effectiveTenantId;
  const isConsentFlow =
    !!consentIdFromUrl ||
    searchParams.get("prompt") === "consent" ||
    searchParams.get("prompt") === "register";

  const consentBrandLogo =
    headerBranding?.logoUrl ||
    (isConsentFlow
      ? searchParams.get("tenant_logo") ||
        searchParams.get("tenantLogo") ||
        currentTenant?.logo
      : null);
  const consentBrandName =
    headerBranding?.name ||
    (isConsentFlow
      ? searchParams.get("tenant_name") ||
        searchParams.get("tenantName") ||
        currentTenant?.name
      : null);
  const showConsentTenantHeader =
    isConsentFlow && (consentBrandLogo || consentBrandName);

  // REACT HOOK FORM
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    mode: "onSubmit",
  });

  // password.current = watch("password", "");

  // MUTATION & USEQUERY
  const { mutate, isLoading } = useMutation(
    (data) => {
      return axios.post("/register", data, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });
    },
    {
      onSuccess: (response) => {
        const registrationData = response?.data?.data;

        if (registrationData) {
          // Extract auth tokens from registration response
          const authData = {
            access_token: registrationData.access_token,
            id_token: registrationData.id_token,
            refresh_token: registrationData.refresh_token,
            token_type: registrationData.token_type,
            expires_in: registrationData.expires_in,
          };

          // Set auth context with tokens and tenant
          if (tenantId) {
            setAuthWithTenant(authData, tenantId);
          } else {
            setAuth(authData);
          }

          enqueueSnackbar(`You're successfully registered!`, {
            variant: "success",
            preventDuplicate: true,
            autoHideDuration: 2000,
          });

          navigatePostAuth(navigate, { redirectUri, location });
        }
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.error?.message ||
          error?.message ||
          "Network Error!",
          {
            variant: "error",
            preventDuplicate: true,
            autoHideDuration: 2000,
          }
        );
      },
    }
  );
  const { isFetching: otpIsFetching } = useQuery(
    [
      "otp_on_registration",
      userData.current?.phone,
      tenantId,
      clientIdFromUrl,
    ],
    () =>
      axios.get("/otp", {
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          type: "signup",
          phone: userData.current.phone,
          tenant_id: tenantId,
          ...(clientIdFromUrl && { client_id: clientIdFromUrl }),
        },
      }),
    {
      enabled: requestOtp,
      onSuccess: () => {
        enqueueSnackbar(`Code sent to your phone!`, {
          variant: "success",
          preventDuplicate: true,
          autoHideDuration: 2000,
        });
        cooldownHook.startCooldown();
        setRequestOtp(false);
        queryClient.clear();
      },
      onError: (err) => {
        enqueueSnackbar(
          err?.response?.data?.error?.message ||
          err?.message ||
          "Network Error!",
          {
            variant: "error",
            preventDuplicate: true,
            autoHideDuration: 2000,
          }
        );
        // Keep the user on the registration form when OTP request fails.
        setOpenDrawer(false);
        setRequestOtp(false);
      },
    }
  );
  const {
    isFetching: tosLinksIsFetching,
    isLoading: tosLinksIsLoading,
    isFetched: tosLinksIsFetched,
  } = useQuery(
    ["tos-pp-url", consentIdFromUrl, clientIdFromUrl, isConsentFlow],
    async () => {
      if (!hasClientAndTenant) return null;
      return axios.get("/clients/tos-pp-url", {
        params: {
          client_id: clientIdFromUrl,
          tenant_id: effectiveTenantId,
        },
      });
    },
    {
      enabled: hasClientAndTenant,
      retry: false,
      onSuccess: (response) => {
        if (!response) {
          setTosAndPPUrl(null);
          return;
        }
        const data = response?.data?.data || response?.data;
        const privacyPolicyUrl = data?.privacy_policy_url || "";
        const termsOfServiceUrl = data?.terms_of_service_url || "";
        if (privacyPolicyUrl || termsOfServiceUrl) {
          setTosAndPPUrl({
            privacy_policy_url: privacyPolicyUrl,
            terms_of_service_url: termsOfServiceUrl,
          });
          return;
        }
        setTosAndPPUrl(null);
      },
      onError: () => {
        // Intentionally hide the checkbox when no TOS/PP data can be fetched.
        setTosAndPPUrl(null);
      },
    }
  );

  // EVENT HANDLERS & HELPERS
  const handleClick = () => {
    if (redirectUri) {
      navigate(redirectUri, { replace: true });
    } else {
      navigate(`/login${location.search || ""}`, {
        replace: true,
        state: location.state,
      });
    }
  };
  const handleRegister = (form) => {
    if (hasClientAndTenant && (tosLinksIsFetching || tosLinksIsLoading)) {
      enqueueSnackbar("Please wait while loading Privacy Policy and Terms.", {
        variant: "info",
        preventDuplicate: true,
        autoHideDuration: 2500,
      });
      return;
    }

    if (tosAndPPUrl && !agreeToTerms) {
      enqueueSnackbar(
        "Please agree to the Privacy Policy and Terms to continue.",
        {
          variant: "warning",
          preventDuplicate: true,
          autoHideDuration: 2500,
        }
      );
      return;
    }

    // If no tenant_id in URL, show tenant selection
    if (!tenantId) {
      setUserIdentifier(form?.phone || form?.email);
      setIdentifierType(form?.phone ? "phone" : "email");
      setShowTenantSelection(true);
      userData.current = form; // Store form data for later use
      if (hasClientAndTenant) setPendingOtpAfterTenantSelect(true);
      return;
    }

    // Otherwise proceed with normal OTP request
    setRequestOtp(true);
    setOpenDrawer(true);
    setPhone(form?.phone);
    userData.current = form;
  };
  const handleSignup = (form) => {
    userData.current = { ...userData.current, otp: form.otp };
    const dataToBeSent =
      file !== ""
        ? { ...userData.current, profile_picture: file }
        : userData.current;
    const payload = tenantId
      ? { ...dataToBeSent, tenant_id: tenantId }
      : dataToBeSent;
    mutate(payload);
  };

  // USEEFFECT
  // Ensure we don't auto-redirect with an existing session from another tenant
  useEffect(() => {
    setAuth({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle tenant selection
  const handleTenantSelected = (tenant) => {
    // After tenant selection, proceed with OTP request
    setShowTenantSelection(false);
    setPhone(userData.current?.phone);

    if (isConsentFlow) {
      // Ensure the user checks the checkbox (if tos/pp exists) before continuing.
      setAgreeToTerms(false);
      setPendingOtpAfterTenantSelect(true);
    } else {
      setRequestOtp(true);
      setOpenDrawer(true);
    }
  };

  useEffect(() => {
    if (!pendingOtpAfterTenantSelect) return;
    if (!isConsentFlow) return;
    // Wait until tos/pp fetch completes (success or error) so we know whether
    // we need a checkbox or can continue immediately.
    if (!tosLinksIsFetched) return;

    if (tosAndPPUrl && !agreeToTerms) return;

    setPendingOtpAfterTenantSelect(false);
    setRequestOtp(true);
    setOpenDrawer(true);
  }, [
    pendingOtpAfterTenantSelect,
    isConsentFlow,
    tosLinksIsFetched,
    tosAndPPUrl,
    agreeToTerms,
  ]);

  const handleBackFromTenantSelection = () => {
    setShowTenantSelection(false);
    setUserIdentifier("");
    userData.current = {};
  };

  //RENDER
  return showTenantSelection ? (
    <TenantSelection
      userIdentifier={userIdentifier}
      identifierType={identifierType}
      onBack={handleBackFromTenantSelection}
      onTenantSelected={handleTenantSelected}
    />
  ) : (
    <>
      {openDrawer && !otpIsFetching ? (
        <Dialog
          open={openDrawer}
          maxWidth="xs"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.1)",
              borderRadius: 3,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.15)",
              backdropFilter: "blur(2px)",
            },
          }}
          PaperProps={{
            sx: {
              width: { xs: "calc(100% - 32px)", sm: "450px" },
              maxWidth: "450px",
              m: { xs: 2, sm: 3 },
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              px: 3,
              pt: 3,
              pb: 2,
            }}
          >
            {showConsentTenantHeader && consentBrandLogo ? (
              <Avatar
                src={consentBrandLogo}
                alt=""
                variant="rounded"
                imgProps={{ style: { objectFit: "contain" } }}
                sx={{
                  width: { xs: 60, sm: 80 },
                  height: { xs: 60, sm: 80 },
                  mb: 2,
                  borderRadius: 2.5,
                }}
              />
            ) : showConsentTenantHeader && consentBrandName ? (
              <Avatar
                sx={{
                  width: { xs: 60, sm: 80 },
                  height: { xs: 60, sm: 80 },
                  mb: 2,
                  borderRadius: 2.5,
                  fontSize: { xs: "1.5rem", sm: "2rem" },
                  fontWeight: 700,
                }}
              >
                {String(consentBrandName).trim().charAt(0) || "?"}
              </Avatar>
            ) : (
              (tenantLogo || !hideDefaultBranding) && (
                <Avatar
                  sx={{
                    width: { xs: 60, sm: 80 },
                    height: { xs: 60, sm: 80 },
                    mb: 2,
                    borderRadius: 2.5,
                  }}
                  src={tenantLogo || logo}
                />
              )
            )}
            <Typography
              sx={{
                fontSize: { xs: 18, sm: 20 },
                fontWeight: 600,
                color: "text.primary",
                textAlign: "center",
              }}
            >
              Verify Your Phone
            </Typography>
          </DialogTitle>
          <DialogContent
            sx={{
              px: 3,
              pb: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 14, sm: 15 },
                mb: 3,
                textAlign: "center",
                color: "text.secondary",
                lineHeight: 1.6,
              }}
            >
              Please enter the code sent to{" "}
              <strong style={{ color: "primary.main" }}>{phone}</strong> via SMS
            </Typography>
            <Box sx={{ mb: 3 }}>
              <CustomPinInput
                handleOnSubmit={handleSignup}
                setInputValue={setInputValue}
              />
            </Box>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: (theme) => theme.palette.mode === 'light' ? "primary.lighter" : alpha(theme.palette.primary.main, 0.1),
                border: "1px solid",
                borderColor: "primary.main",
              }}
            >
              <OtpResendButton
                cooldownHook={cooldownHook}
                onResend={() => setRequestOtp(true)}
                isLoading={otpIsFetching}
                size="small"
                showMessage={true}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              pb: 3,
              pt: 2,
            }}
          >
            <Box
              sx={{
                display: { md: "flex", xs: "none" },
                gap: 1.5,
                width: "100%",
              }}
            >
              <Button
                size="small"
                variant="outlined"
                fullWidth
                onClick={() => setOpenDrawer(false)}
                sx={{
                  py: 1.5,
                  borderColor: "#dee2e6",
                  color: "#495057",
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 15,
                  ":hover": {
                    borderColor: "#adb5bd",
                    bgcolor: "#f8f9fa",
                  },
                }}
              >
                Back
              </Button>
              <Button
                size="small"
                variant="contained"
                fullWidth
                type="submit"
                onClick={() => handleSignup({ otp: inputValue })}
                disabled={isLoading || inputValue?.length !== 6}
                sx={{
                  py: 1.5,
                  bgcolor: "secondary.main",
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 15,
                  boxShadow: "0 3px 5px 2px rgba(29, 36, 38, .2)",
                  "&:hover": {
                    bgcolor: "#000",
                    transform: { xs: "none", sm: "translateY(-1px)" },
                    boxShadow: {
                      xs: "0 3px 5px 2px rgba(29, 36, 38, .2)",
                      sm: "0 4px 8px 2px rgba(29, 36, 38, .3)",
                    },
                  },
                  "&:disabled": {
                    bgcolor: "#9e9e9e",
                    color: "#fff",
                  },
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                Continue
                {isLoading && (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: "secondary.contrastText",
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                )}
              </Button>
            </Box>
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                flexDirection: "column",
                gap: 1.5,
                width: "100%",
              }}
            >
              <Button
                size="small"
                variant="contained"
                fullWidth
                type="submit"
                onClick={() => handleSignup({ otp: inputValue })}
                disabled={isLoading || inputValue?.length !== 6}
                sx={{
                  py: 1.5,
                  bgcolor: "secondary.main",
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 15,
                  boxShadow: "0 3px 5px 2px rgba(29, 36, 38, .2)",
                  "&:disabled": {
                    bgcolor: "#9e9e9e",
                    color: "#fff",
                  },
                }}
              >
                Continue
                {isLoading && (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: "secondary.contrastText",
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                )}
              </Button>
              <Button
                size="small"
                variant="outlined"
                fullWidth
                onClick={() => setOpenDrawer(false)}
                sx={{
                  py: 1.5,
                  borderColor: "divider",
                  color: "text.secondary",
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 15,
                  ":hover": {
                    borderColor: "primary.main",
                    bgcolor: "action.hover",
                  },
                }}
              >
                Back
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      ) : (
        <Dialog
          open={!openDrawer}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.1)",
              borderRadius: 3,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.15)",
              backdropFilter: "blur(2px)",
            },
          }}
          PaperProps={{
            sx: {
              width: { xs: "calc(100% - 32px)", sm: "600px" },
              maxWidth: "600px",
              m: { xs: 2, sm: 3 },
            },
          }}
          component="form"
          onSubmit={handleSubmit(handleRegister)}
        >
          <DialogContent
            sx={{
              px: 3,
              pt: 3,
              pb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 3,
              }}
            >
              {showConsentTenantHeader && consentBrandLogo ? (
                <Box
                  component="img"
                  src={consentBrandLogo}
                  alt=""
                  sx={{
                    width: { xs: 60, sm: 80 },
                    height: { xs: 60, sm: 80 },
                    objectFit: "contain",
                    mb: 1.5,
                    borderRadius: 2.5,
                  }}
                />
              ) : showConsentTenantHeader && consentBrandName && !consentBrandLogo ? (
                <Avatar
                  sx={{
                    width: { xs: 60, sm: 80 },
                    height: { xs: 60, sm: 80 },
                    mb: 1.5,
                    borderRadius: 2.5,
                    fontSize: { xs: "1.5rem", sm: "2rem" },
                    fontWeight: 700,
                  }}
                >
                  {String(consentBrandName).trim().charAt(0) || "?"}
                </Avatar>
              ) : null}
              <Typography
                sx={{
                  fontSize: { xs: 22, sm: 24 },
                  fontWeight: 600,
                  color: "text.primary",
                  textAlign: "center",
                  mb: 1,
                }}
              >
                Create Your Account
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 13, sm: 14 },
                  color: "text.secondary",
                  textAlign: "center",
                  mb: 3,
                  lineHeight: 1.5,
                }}
              >
                Join us and get started with your journey
              </Typography>
              <ProfilePicture
                edit={true}
                isOpen={open}
                setIsOpen={setOpen}
                imageSrc={file}
                setFile={setFile}
              />
            </Box>
            <Controller
              name="first_name"
              control={control}
              rules={{
                required: "First Name is required",
              }}
              render={({ field }) => (
                <TextField
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                  label="First Name"
                  fullWidth
                  autoComplete="first_name"
                  defaultValue={userData.current?.first_name}
                  error={!!errors?.first_name}
                  helperText={
                    errors?.first_name ? errors.first_name.message : null
                  }
                  size="small"
                  {...field}
                />
              )}
            />
            <Controller
              name="middle_name"
              control={control}
              rules={{
                required: "Middle Name is required",
              }}
              render={({ field }) => (
                <TextField
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                  variant="outlined"
                  label="Middle Name"
                  fullWidth
                  autoComplete="middle_name"
                  defaultValue={userData.current?.middle_name}
                  error={!!errors?.middle_name}
                  helperText={
                    errors?.middle_name ? errors.middle_name.message : null
                  }
                  size="small"
                  {...field}
                />
              )}
            />
            <Controller
              name="last_name"
              control={control}
              rules={{
                required: "Last Name is required",
              }}
              render={({ field }) => (
                <TextField
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                  variant="outlined"
                  label="Last Name"
                  fullWidth
                  autoComplete="last_name"
                  defaultValue={userData.current?.last_name}
                  error={!!errors?.last_name}
                  helperText={
                    errors?.last_name ? errors.last_name.message : null
                  }
                  size="small"
                  {...field}
                />
              )}
            />
            <Controller
              name="gender"
              control={control}
              render={({ field }) => (
                <TextField
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                  variant="outlined"
                  label="Gender"
                  fullWidth
                  select
                  size="small"
                  autoComplete="gender"
                  defaultValue={userData.current?.gender}
                  error={!!errors?.gender}
                  helperText={errors?.gender ? errors.gender.message : null}
                  {...field}
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="PREFER_NOT_TO_SAY">Prefer Not To Say</MenuItem>
                </TextField>
              )}
            />
            <Controller
              name="phone"
              control={control}
              rules={{
                required: "Phone is required",
                pattern: {
                  value: phoneRegex,
                  message: "Invalid input",
                },
              }}
              render={({ field }) => (
                <TextField
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                  variant="outlined"
                  label="Phone Number"
                  fullWidth
                  autoComplete="phone"
                  defaultValue={userData.current?.phone}
                  error={!!errors?.phone}
                  helperText={errors?.phone ? errors.phone.message : null}
                  size="small"
                  placeholder="Enter your phone number"
                  {...field}
                />
              )}
            />
            {/* <Controller
              name="email"
              control={control}
              rules={{
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              }}
              render={({ field }) => (
                <TextField
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                  variant="outlined"
                  label="Email (Optional)"
                  fullWidth
                  autoComplete="email"
                  defaultValue={userData.current?.email}
                  error={!!errors?.email}
                  helperText={errors?.email ? errors.email.message : null}
                  size="small"
                  placeholder="your.email@example.com"
                  {...field}
                />
              )}
            /> */}
            {/* <Controller
              name="password"
              control={control}
              rules={{
                required: "Password is required",
                minLength: {
                  value: 6,
                  message: "At least 6 characters is required.",
                },
                maxLength: {
                  value: 32,
                  message: "The maximum characters allowed is 32.",
                },
              }}
              render={({ field }) => (
                <TextField
                  id="password-input"
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                  label="Password"
                  fullWidth
                  type={inputType}
                  autoComplete="current-password"
                  defaultValue={userData.current?.password}
                  error={!!errors?.password}
                  helperText={errors?.password ? errors.password.message : null}
                  size="small"
                  placeholder="At least 6 characters"
                  {...field}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setShowPassword(
                              showPassword === false ? true : false
                            );
                            setInputType(
                              showPassword === false ? "text" : "password"
                            );
                          }}
                        >
                          {showPassword ? (
                            <Visibility sx={{ fontSize: "inherit" }} />
                          ) : (
                            <VisibilityOff sx={{ fontSize: "inherit" }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            /> */}
            {/* <Controller
              name="confirm_password"
              control={control}
              rules={{
                validate: (value) =>
                  value === password.current || "The passwords do not match",
              }}
              render={({ field }) => (
                <TextField
                  id="confirm-password-input"
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                  label="Confirm Password"
                  fullWidth
                  type={confirmInputType}
                  defaultValue={userData.current?.confirm_password}
                  autoComplete="confirm-current-password"
                  error={!!errors?.confirm_password}
                  helperText={
                    errors?.confirm_password
                      ? errors.confirm_password.message
                      : null
                  }
                  size="small"
                  placeholder="Re-enter your password"
                  {...field}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setShowConfirmPassword(
                              showConfirmPassword === false ? true : false
                            );
                            setConfirmInputType(
                              showConfirmPassword === false
                                ? "text"
                                : "password"
                            );
                          }}
                        >
                          {showConfirmPassword ? (
                            <Visibility sx={{ fontSize: "inherit" }} />
                          ) : (
                            <VisibilityOff sx={{ fontSize: "inherit" }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            /> */}
            {tosAndPPUrl && (
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreeToTerms}
                      onChange={(e) => setAgreeToTerms(e.target.checked)}
                      sx={{ p: 0.5, mt: 0.25 }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      I agree to the{" "}
                      {tosAndPPUrl?.privacy_policy_url && (
                        <Link
                          href={tosAndPPUrl.privacy_policy_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Privacy Policy
                        </Link>
                      )}
                      {tosAndPPUrl?.privacy_policy_url &&
                      tosAndPPUrl?.terms_of_service_url
                        ? " and "
                        : ""}
                      {tosAndPPUrl?.terms_of_service_url && (
                        <Link
                          href={tosAndPPUrl.terms_of_service_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Terms of Service
                        </Link>
                      )}
                      .
                    </Typography>
                  }
                  sx={{
                    alignItems: "center",
                    m: 0,
                    gap: 1,
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              px: 2,
              pb: 3,
              pt: 1,
            }}
          >
            {/* Desktop Buttons */}
            <Box
              sx={{
                display: { md: "flex", xs: "none" },
                gap: 1.5,
                width: "100%",
              }}
            >
              <Button
                size="small"
                variant="outlined"
                fullWidth
                onClick={() => handleClick()}
                sx={{
                  py: 1,
                  borderColor: "#dee2e6",
                  color: "#495057",
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 15,
                  ":hover": {
                    borderColor: "#adb5bd",
                    bgcolor: "#f8f9fa",
                  },
                }}
              >
                Back
              </Button>
              <Button
                size="small"
                variant="contained"
                fullWidth
                type="submit"
                disabled={otpIsFetching || (isConsentFlow && tosLinksIsFetching)}
                sx={{
                  py: 1,
                  bgcolor: "primary.main",
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 15,
                  boxShadow: "0 3px 5px 2px rgba(255, 170, 0, .3)",
                  "&:hover": {
                    bgcolor: "#ff9500",
                    transform: { xs: "none", sm: "translateY(-1px)" },
                    boxShadow: {
                      xs: "0 3px 5px 2px rgba(255, 170, 0, .3)",
                      sm: "0 4px 8px 2px rgba(255, 170, 0, .4)",
                    },
                  },
                  "&:disabled": {
                    bgcolor: "#9e9e9e",
                    color: "#fff",
                  },
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                {otpIsFetching ? (
                  <CircularProgress size={24} sx={{ color: "#fff" }} />
                ) : (
                  "Register"
                )}
              </Button>
            </Box>
            {/* Mobile Buttons */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                flexDirection: "column",
                gap: 1.5,
                width: "100%",
              }}
            >
              <Button
                size="small"
                variant="contained"
                fullWidth
                type="submit"
                disabled={otpIsFetching || (isConsentFlow && tosLinksIsFetching)}
                sx={{
                  py: 1.5,
                  bgcolor: "primary.main",
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 15,
                  boxShadow: "0 3px 5px 2px rgba(255, 170, 0, .3)",
                  "&:disabled": {
                    bgcolor: "#9e9e9e",
                    color: "#fff",
                  },
                }}
              >
                {otpIsFetching ? (
                  <CircularProgress size={24} sx={{ color: "#fff" }} />
                ) : (
                  "Register"
                )}
              </Button>
              <Button
                size="small"
                variant="outlined"
                fullWidth
                onClick={() => handleClick()}
                sx={{
                  py: 1.5,
                  borderColor: "#dee2e6",
                  color: "#495057",
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 15,
                  ":hover": {
                    borderColor: "#adb5bd",
                    bgcolor: "#f8f9fa",
                  },
                }}
              >
                Back
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
export default Register;
